import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { PlanType } from './TiersAndPricing';

interface TierCardProps {
  tier: PlanType;
  title: string;
  price: string;
  period: string;
  features: string[];
  badge?: string;
  isPermanentHighlight?: boolean;
  currentPlan?: string;
  selectedPlan?: PlanType | null;
  hideCurrentPlanHighlight?: boolean;
  showChoosePlanButtons?: boolean;
  hasActiveSubscription?: boolean;
  trialExpired?: boolean;
  isLoading?: boolean;
  hideButtons?: boolean;
  scheduledPlanChange?: {
    to: string;
    effectiveDate: string;
    toDisplayName: string;
  } | null;
  onPlanSelect?: (planType: PlanType) => void;
  onManageSubscription?: () => void;
}

export function TierCard({
  tier,
  title,
  price,
  period,
  features,
  badge,
  isPermanentHighlight = false,
  currentPlan,
  selectedPlan,
  hideCurrentPlanHighlight = false,
  showChoosePlanButtons = false,
  hasActiveSubscription = false,
  trialExpired = false,
  isLoading = false,
  hideButtons = false,
  scheduledPlanChange,
  onPlanSelect,
  onManageSubscription,
}: TierCardProps) {
  const { t } = useTranslation();

  const handlePlanSelect = () => {
    if (onPlanSelect && !isLoading) {
      onPlanSelect(tier);
    }
  };

  // Button rendering functions for different states
  const renderChoosePlanButton = () => (
    <Button
      className='w-full bg-blue-600 hover:bg-blue-700 text-white'
      onClick={handlePlanSelect}
      disabled={isLoading || selectedPlan === tier}
      variant={selectedPlan === tier ? 'outline' : 'default'}
    >
      {selectedPlan === tier
        ? t('subscription.dialog.buttons.currentChoice')
        : t('subscription.dialog.buttons.chooseThisPlan')}
    </Button>
  );

  const renderManagePlanButton = () => (
    <div className='space-y-2'>
      <Button
        className='w-full'
        variant='outline'
        onClick={onManageSubscription || undefined}
        disabled={isLoading}
      >
        {t('subscription.dialog.buttons.managePlan')}
      </Button>
      {/* Special handling for POWER tier scheduled downgrades */}
      {tier === 'POWER' && scheduledPlanChange && (
        <p className='text-xs text-orange-600 dark:text-orange-400'>
          {t('subscription.dialog.scheduledDowngrade.message', {
            plan: scheduledPlanChange.toDisplayName,
            date: new Date(
              scheduledPlanChange.effectiveDate,
            ).toLocaleDateString(),
          })}
        </p>
      )}
    </div>
  );

  const renderScheduledChangeButton = () => (
    <div className='space-y-2'>
      <Button className='w-full text-xs' variant='outline' disabled>
        {scheduledPlanChange &&
          t('subscription.dialog.buttons.scheduledFor', {
            date: new Date(
              scheduledPlanChange.effectiveDate,
            ).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            }),
          })}
      </Button>
      <p className='text-xs text-gray-600 dark:text-gray-400'>
        {t('subscription.dialog.scheduledChange.description')}
      </p>
    </div>
  );

  const renderTrialButton = () =>
    hasActiveSubscription ? (
      <Button
        className='w-full bg-blue-600 hover:bg-blue-700 text-white'
        onClick={handlePlanSelect}
        disabled={isLoading}
      >
        {t('subscription.dialog.buttons.switchToThisPlan')}
      </Button>
    ) : (
      <Button
        className='w-full'
        variant='outline'
        onClick={onManageSubscription || undefined}
        disabled={isLoading}
      >
        {t('subscription.dialog.buttons.manageTrial')}
      </Button>
    );

  const renderDefaultButton = () => (
    <Button
      className='w-full bg-blue-600 hover:bg-blue-700 text-white'
      onClick={handlePlanSelect}
      disabled={isLoading}
    >
      {currentPlan === 'FREE_TRIAL' && hasActiveSubscription
        ? t('subscription.dialog.buttons.switchToThisPlan')
        : currentPlan === 'FREE_TRIAL' || trialExpired
          ? t('subscription.dialog.buttons.startFreeTrial')
          : t('subscription.dialog.buttons.switchPlan')}
    </Button>
  );

  const renderButtonContent = () => {
    if (showChoosePlanButtons) return renderChoosePlanButton();
    if (currentPlan === tier && !trialExpired) return renderManagePlanButton();
    if (hasScheduledChange) return renderScheduledChangeButton();
    if (currentPlan === 'FREE_TRIAL' && tier === 'POWER' && !trialExpired)
      return renderTrialButton();
    return renderDefaultButton();
  };

  // Determine if this tier should be highlighted
  const shouldHighlight =
    isPermanentHighlight ||
    (!hideCurrentPlanHighlight &&
      (currentPlan === tier ||
        (tier === 'POWER' && currentPlan === 'FREE_TRIAL'))) ||
    selectedPlan === tier;

  // Check if this tier has a scheduled change
  const hasScheduledChange = scheduledPlanChange?.to === tier;

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-md px-3 pt-2 pb-4 relative ${
        shouldHighlight ? 'border-2 border-blue-500' : ''
      }`}
    >
      {/* Badge for special tiers */}
      {badge && (
        <div className='absolute top-0 right-0 left-0 bg-blue-500 text-white text-xs font-bold py-1 text-center'>
          {badge}
        </div>
      )}

      {/* Title */}
      <h3 className={`text-xl font-bold mb-2 ${badge ? 'mt-4' : ''}`}>
        {title}
      </h3>

      {/* Price */}
      <div className='text-3xl font-bold mb-4'>
        <span>{price}</span>
        <span className='text-sm text-gray-500 dark:text-gray-400 ml-1'>
          {period}
        </span>
      </div>

      {/* Features */}
      <ul className='space-y-1 mb-8 text-left text-sm'>
        {features.map((feature, index) => (
          <li key={index} className='flex items-center'>
            <svg
              className='h-5 w-5 text-green-500 mr-2 flex-shrink-0'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              aria-hidden='true'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M5 13l4 4L19 7'
              />
            </svg>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {/* Button Section */}
      {!hideButtons && (
        <div className='text-center mt-auto'>{renderButtonContent()}</div>
      )}
    </div>
  );
}
